/**
 * AdminNoteAuditsList Component
 *
 * Admin version of note audits list with optional organization filter
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Business as OrgIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DescriptionOutlined as EmptyIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { useAdminNoteAuditsList } from '../../hooks/admin';
import { NoteAuditRow } from '../notes/NoteAuditRow';

interface AdminNoteAuditsListProps {
  /** Filter by organization ID */
  organizationId?: string;
  /** Organization name for display */
  organizationName?: string | null;
  /** Callback to clear organization filter */
  onClearFilter?: () => void;
}

export const AdminNoteAuditsList: React.FC<AdminNoteAuditsListProps> = ({
  organizationId,
  organizationName,
  onClearFilter,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { audits, total, page, perPage, hasMore, isLoading, error, refetch, isEmpty } =
    useAdminNoteAuditsList({ organizationId });

  const handleViewAudit = useCallback(
    (noteId: string) => {
      // Navigate to admin-v2 note detail page
      navigate(`/admin-v2/note/${noteId}`);
    },
    [navigate],
  );

  const handleOpenNote = useCallback(
    (noteId: string) => {
      // Navigate to admin-v2 note view
      navigate(`/admin-v2/note/${noteId}`);
    },
    [navigate],
  );

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const hasPrevious = page > 1;

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading audits</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Note Audits
          </Typography>
          {!isLoading && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {total} {total === 1 ? 'audit' : 'audits'}
              {organizationId ? ' in selected organization' : ' across all organizations'}
            </Typography>
          )}
        </Box>
        {organizationId && (
          <Chip
            icon={<OrgIcon sx={{ fontSize: 16 }} />}
            label={organizationName || organizationId.slice(0, 8)}
            onDelete={onClearFilter}
            color="primary"
            variant="outlined"
            size="small"
          />
        )}
      </Box>

      {/* Table Scroll Container */}
      <Box sx={{ overflowX: 'auto' }}>
        {/* Table Header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 70px 80px 180px 140px 70px 100px 60px 60px 1fr 100px',
            gap: 2,
            alignItems: 'center',
            px: 2,
            py: 1.5,
            bgcolor: theme.palette.grey[50],
            borderRadius: '8px 8px 0 0',
            border: `1px solid ${theme.palette.grey[200]}`,
            borderBottom: 'none',
            minWidth: 1080,
          }}
        >
          <Box /> {/* Icon placeholder */}
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
          >
            Date
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
          >
            Status
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
          >
            Patient
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
          >
            Provider
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
          >
            CPT
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
          >
            Flags
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: theme.palette.text.secondary, textAlign: 'center' }}
          >
            Score
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
          >
            Quality
          </Typography>
          <Box /> {/* Spacer */}
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: theme.palette.text.secondary }}
          >
            Actions
          </Typography>
        </Box>

        {/* List Container */}
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: '0 0 8px 8px',
            overflow: 'hidden',
            minWidth: 1080,
          }}
        >
          {/* Loading State */}
          {isLoading && audits.length === 0 ? (
            <Box>
              {Array.from({ length: 5 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns:
                      '40px 70px 80px 180px 140px 70px 100px 60px 60px 1fr 100px',
                    gap: 2,
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    minWidth: 1080,
                    borderBottom: `1px solid ${theme.palette.grey[100]}`,
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Skeleton variant="rounded" width={40} height={40} />
                  <Skeleton width={50} height={20} />
                  <Skeleton width={60} height={24} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton width={100} height={20} />
                  </Box>
                  <Skeleton width={100} height={20} />
                  <Skeleton width={50} height={20} />
                  <Skeleton width={80} height={24} />
                  <Skeleton width={40} height={20} />
                  <Skeleton width={50} height={24} />
                  <Box />
                  <Skeleton width={80} height={32} />
                </Box>
              ))}
            </Box>
          ) : isEmpty ? (
            /* Empty State */
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 3,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: theme.palette.grey[100],
                  mb: 2,
                }}
              >
                <EmptyIcon sx={{ fontSize: 32, color: theme.palette.grey[400] }} />
              </Box>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
                No audits found
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}
              >
                Audit data will appear here once notes are audited
              </Typography>
            </Box>
          ) : (
            /* Audit List */
            <>
              <Box sx={{ position: 'relative' }}>
                {isLoading && audits.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: alpha(theme.palette.background.paper, 0.7),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}

                {audits.map(audit => (
                  <NoteAuditRow
                    key={audit.audit.note_audit_id}
                    audit={audit}
                    onViewAudit={handleViewAudit}
                    onOpenNote={handleOpenNote}
                  />
                ))}
              </Box>

              {/* Pagination */}
              {total > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderTop: `1px solid ${theme.palette.grey[100]}`,
                    bgcolor: alpha(theme.palette.grey[50], 0.5),
                  }}
                >
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Page {page} of {totalPages}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" disabled={!hasPrevious || isLoading}>
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton size="small" disabled={!hasMore || isLoading}>
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default AdminNoteAuditsList;

